/**
 * PaywallVariablesMenu - Dropdown menu for selecting paywall template variables
 *
 * Displays a list of available paywall variables (@price, @duration, etc.)
 * with descriptions and examples. Used by:
 * - TiptapTemplateEditor (for title/subtitle fields)
 * - PaywallVariableTextField (for simple text fields)
 *
 * Features:
 * - Keyboard navigation (ArrowUp/Down, Enter, Escape)
 * - Category grouping
 * - Variable preview with examples
 *
 * @module reactFlow/components/shared/PaywallVariablesMenu
 */

import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Typography,
  Popper,
  ClickAwayListener,
  Box,
} from '@mui/material';
import {
  PAYWALL_VARIABLES,
  getVariablesByCategory,
  CATEGORY_LABELS,
  type PaywallVariable,
} from '../../config/paywallVariablesConfig';

/**
 * Props for PaywallVariablesMenu
 */
export interface PaywallVariablesMenuProps {
  /** Anchor element or virtual element for positioning */
  anchorEl: HTMLElement | { getBoundingClientRect: () => DOMRect } | null;

  /** Whether the menu is open */
  open: boolean;

  /** Callback when menu should close */
  onClose: () => void;

  /** Callback when a variable is selected */
  onSelect: (variable: PaywallVariable) => void;

  /** Optional placement */
  placement?: 'bottom-start' | 'bottom' | 'bottom-end' | 'top-start' | 'top' | 'top-end';
}

/**
 * Ref handle for keyboard navigation
 */
export interface PaywallVariablesMenuRef {
  /** Handle keyboard events, returns true if handled */
  onKeyDown: (event: KeyboardEvent | React.KeyboardEvent) => boolean;
}

const menuPaperSx = {
  width: 300,
  maxHeight: 350,
  overflow: 'auto',
  boxShadow: 3,
  borderRadius: 1,
};

const listSubheaderSx = {
  bgcolor: 'action.hover',
  lineHeight: '32px',
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: 'text.secondary',
};

const variableNameSx = {
  fontFamily: 'monospace',
  color: 'primary.main',
  fontWeight: 500,
};

const exampleSx = {
  color: 'text.secondary',
  fontSize: '0.75rem',
  ml: 1,
};

/**
 * PaywallVariablesMenu - Dropdown for selecting paywall variables
 */
export const PaywallVariablesMenu = forwardRef<PaywallVariablesMenuRef, PaywallVariablesMenuProps>(
  function PaywallVariablesMenu({ anchorEl, open, onClose, onSelect, placement = 'bottom-start' }, ref) {
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Track if selection is in progress (prevents ClickAwayListener from closing during click)
    const isSelectingRef = useRef(false);

    // Reset selection when menu opens
    useEffect(() => {
      if (open) {
        setSelectedIndex(0);
        isSelectingRef.current = false;
      }
    }, [open]);

    // Expose keyboard navigation to parent
    useImperativeHandle(
      ref,
      () => ({
        onKeyDown: (event: KeyboardEvent | React.KeyboardEvent) => {
          if (!open) return false;

          switch (event.key) {
            case 'ArrowDown':
              event.preventDefault();
              setSelectedIndex((prev) => Math.min(prev + 1, PAYWALL_VARIABLES.length - 1));
              return true;

            case 'ArrowUp':
              event.preventDefault();
              setSelectedIndex((prev) => Math.max(prev - 1, 0));
              return true;

            case 'Enter':
              event.preventDefault();
              if (PAYWALL_VARIABLES[selectedIndex]) {
                onSelect(PAYWALL_VARIABLES[selectedIndex]);
              }
              return true;

            case 'Escape':
              event.preventDefault();
              onClose();
              return true;

            case 'Tab':
              onClose();
              return false;

            default:
              return false;
          }
        },
      }),
      [open, selectedIndex, onSelect, onClose]
    );

    // Handle click on variable (with guard to prevent race condition with ClickAwayListener)
    const handleVariableClick = useCallback(
      (event: React.MouseEvent, variable: PaywallVariable) => {
        event.stopPropagation();
        event.preventDefault();
        isSelectingRef.current = true;
        onSelect(variable);
        // Reset flag after event loop completes
        requestAnimationFrame(() => {
          isSelectingRef.current = false;
        });
      },
      [onSelect]
    );

    // Wrapper for onClose that respects selection guard
    const handleClickAway = useCallback(() => {
      if (!isSelectingRef.current) {
        onClose();
      }
    }, [onClose]);

    // Get variables grouped by category
    const variablesByCategory = getVariablesByCategory();
    const categories = Object.keys(variablesByCategory);

    // Calculate flat index for each variable (for keyboard navigation)
    let flatIndex = 0;
    const getFlatIndex = () => flatIndex++;

    if (!open || !anchorEl) {
      return null;
    }

    return (
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        style={{ zIndex: 1400 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper sx={menuPaperSx} data-paywall-menu data-testid="paywall-variables-menu">
            <List dense disablePadding>
              {categories.map((category) => (
                <React.Fragment key={category}>
                  <ListSubheader sx={listSubheaderSx}>
                    {CATEGORY_LABELS[category] || category}
                  </ListSubheader>
                  {variablesByCategory[category].map((variable) => {
                    const currentIndex = getFlatIndex();
                    return (
                      <ListItem key={variable.name} disablePadding>
                        <ListItemButton
                          selected={selectedIndex === currentIndex}
                          onMouseDown={() => { isSelectingRef.current = true; }}
                          onClick={(event) => handleVariableClick(event, variable)}
                          data-testid={`paywall-var-${variable.name}`}
                          sx={{ py: 0.75 }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography component="span" sx={variableNameSx}>
                                  @{variable.name}
                                </Typography>
                                <Typography component="span" sx={exampleSx}>
                                  {variable.example}
                                </Typography>
                              </Box>
                            }
                            secondary={variable.description}
                            slotProps={{
                              secondary: {
                                variant: 'caption',
                                sx: { mt: 0.25 },
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    );
  }
);

export default PaywallVariablesMenu;
